import "@/lib/design-fonts";
import { useIsMobile } from "@/hooks/use-mobile";
import { operation } from "@/lib/api-client";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Save,
  Globe2,
  Upload,
  ImageIcon,
  X,
  FileText,
  Presentation,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { uploadChatFile, getSignedUrl } from "@/lib/upload";
import { toast } from "sonner";

export const Route = createFileRoute("/design/systems/$systemId")({
  head: () => ({
    meta: [
      { title: "Edit Design System — AskBell" },
      {
        name: "description",
        content:
          "Define a brand style — logo, fonts, palette, voice — and save it for reuse across exports.",
      },
    ],
  }),
  component: SystemEditorPage,
});

type PaletteSwatch = { name?: string; role?: string; hex: string };
type GradientStop = { hex: string; pos: number };
type Gradient = {
  id: string;
  name?: string;
  role?: "hero" | "section" | "accent" | "";
  angle: number;
  stops: GradientStop[];
};
type SizeOverrides = {
  headingPt?: number;
  bodyPt?: number;
  headingLineHeight?: number;
  bodyLineHeight?: number;
};
type Fonts = {
  heading?: string;
  body?: string;
  mono?: string;
  sizes?: { pptx?: SizeOverrides; pdf?: SizeOverrides };
};

// Defaults (in pt for fonts, unitless for line-height)
const SIZE_DEFAULTS: Record<"pptx" | "pdf", Required<SizeOverrides>> = {
  pptx: { headingPt: 36, bodyPt: 16, headingLineHeight: 1.1, bodyLineHeight: 1.5 },
  pdf: { headingPt: 24, bodyPt: 11, headingLineHeight: 1.2, bodyLineHeight: 1.55 },
};
type System = {
  id: string;
  user_id: string;
  name: string;
  is_shared: boolean;
  palette: PaletteSwatch[];
  gradients: Gradient[];
  fonts: Fonts;
  voice: string | null;
  analysis_style: string | null;
  logo_attachment_id: string | null;
};

function gradientCss(g: Gradient): string {
  const stops = [...g.stops]
    .sort((a, b) => a.pos - b.pos)
    .map((s) => `${s.hex} ${Math.round(s.pos * 100)}%`)
    .join(", ");
  return `linear-gradient(${g.angle}deg, ${stops})`;
}

const FONT_PRESETS = [
  "Inter",
  "Georgia",
  "Helvetica Neue",
  "Calibri",
  "Cambria",
  "Roboto",
  "IBM Plex Sans",
  "IBM Plex Serif",
  "Source Sans Pro",
  "Playfair Display",
];

function SystemEditorPage() {
  const { systemId } = Route.useParams();
  const activeSystem=useRef(systemId);
  activeSystem.current=systemId;
  const navigate = useNavigate();
  const isMobile=useIsMobile();
  const [mobilePane,setMobilePane]=useState<"editor"|"preview">("editor");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [system, setSystem] = useState<System | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [previewFormat, setPreviewFormat] = useState<"pptx" | "pdf">("pptx");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Writing-rules validator
  const DEFAULT_SAMPLE = `Summary
Customer satisfaction climbed to 87% this quarter, up 6 points from Q4. The biggest driver was faster onboarding response times.

Findings
- Response time dropped from 14h to 4h after the new triage rotation launched.
- NPS for the SMB segment lags enterprise by 22 points; pricing comes up unprompted in 41% of detractor interviews.
- Renewal intent rose to 78% across surveyed accounts.

Recommendation
Roll the triage rotation to the SMB queue next month and revisit SMB pricing tiers before the renewal cycle opens in July.`;

  const [validatorSample, setValidatorSample] = useState(DEFAULT_SAMPLE);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    score: number;
    summary: string;
    findings: Array<{
      severity: "error" | "warning" | "info";
      rule_source: "voice" | "analysis_style";
      rule: string;
      excerpt: string;
      explanation: string;
      suggestion: string;
    }>;
  } | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);setSystem(null);setLogoUrl(null);setValidationResult(null);setValidating(false);setUploadingLogo(false);
    const load = async () => {
      const { data, error } = await awsClient
        .from("design_systems")
        .select(
          "id, user_id, name, is_shared, palette, gradients, fonts, voice, analysis_style, logo_attachment_id",
        )
        .eq("id", systemId)
        .maybeSingle();
      if (!active) return;
      if (error || !data) {
        toast.error("Couldn't load design system");
        navigate({ to: "/design/systems" });
        return;
      }
      setSystem({
        id: data.id,
        user_id: data.user_id,
        name: data.name,
        is_shared: data.is_shared,
        palette: Array.isArray(data.palette) ? (data.palette as PaletteSwatch[]) : [],
        gradients: Array.isArray((data as any).gradients)
          ? ((data as any).gradients as Gradient[])
          : [],
        fonts: (data.fonts as Fonts) || {},
        voice: data.voice,
        analysis_style: data.analysis_style,
        logo_attachment_id: data.logo_attachment_id,
      });
      setLoading(false);

      setLogoUrl(null);
      if (data.logo_attachment_id) {
        const url=await getSignedUrl(data.logo_attachment_id);
        if(active){setLogoUrl(url);if(!url)toast.error("Couldn't load the saved logo.");}
      }

    };
    void load();
    return () => {
      active = false;
    };
  }, [systemId, navigate]);

  const readOnly = !user || !system || system.id!==systemId || system.user_id !== user.id;

  const saveLock=useRef(false);
  const onSave = async () => {
    if (!system || readOnly || saveLock.current || uploadingLogo) return;
    saveLock.current=true;setSaving(true);
    try {
    await operation("design_system_save",{
        id:system.id,
        name: system.name.trim() || "Untitled system",
        is_shared: system.is_shared,
        palette: system.palette,
        gradients: system.gradients as any,
        fonts: system.fonts,
        voice: system.voice,
        analysis_style: system.analysis_style,
        logo_attachment_id: system.logo_attachment_id,
      });
      toast.success("Saved");
    } catch(error){toast.error("Couldn't save",{description:error instanceof Error?error.message:"Please try again."});}
    finally {saveLock.current=false;setSaving(false);}
  };

  const onUploadLogo = async (file: File) => {
    if (!user || !system || readOnly) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Logo must be an image");
      return;
    }
    setUploadingLogo(true);
    try {
      const att = await uploadChatFile(file, user.id, null, {
        route: "design-system-asset",
        scopeId: system.id,
      });
      if(activeSystem.current!==system.id)return;
      setSystem((prev) => prev?.id===system.id ? { ...prev, logo_attachment_id: att.id ?? null } : prev);
      setLogoUrl(att.signed_url ?? null);
      toast.success("Logo uploaded — remember to save");
    } catch (e: any) {
      if(activeSystem.current===system.id)toast.error("Couldn't upload logo", { description: e?.message });
    } finally {
      if(activeSystem.current===system.id)setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    if (!system || readOnly) return;
    setSystem({ ...system, logo_attachment_id: null });
    setLogoUrl(null);
  };

  const updatePalette = (i: number, patch: Partial<PaletteSwatch>) => {
    setSystem((prev) =>
      prev
        ? {
            ...prev,
            palette: prev.palette.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
          }
        : prev,
    );
  };

  const addSwatch = () => {
    setSystem((prev) =>
      prev
        ? {
            ...prev,
            palette: [...prev.palette, { role: "", name: "", hex: "#888888" }],
          }
        : prev,
    );
  };

  const removeSwatch = (i: number) => {
    setSystem((prev) =>
      prev ? { ...prev, palette: prev.palette.filter((_, idx) => idx !== i) } : prev,
    );
  };

  const updateGradient = (i: number, patch: Partial<Gradient>) => {
    setSystem((prev) =>
      prev
        ? {
            ...prev,
            gradients: prev.gradients.map((g, idx) =>
              idx === i ? { ...g, ...patch } : g,
            ),
          }
        : prev,
    );
  };

  const addGradient = () => {
    setSystem((prev) =>
      prev
        ? {
            ...prev,
            gradients: [
              ...prev.gradients,
              {
                id: crypto.randomUUID().slice(0, 8),
                name: "",
                role: "hero",
                angle: 135,
                stops: [
                  { hex: prev.palette[0]?.hex ?? "#0F766E", pos: 0 },
                  { hex: prev.palette[1]?.hex ?? "#84CC16", pos: 1 },
                ],
              },
            ],
          }
        : prev,
    );
  };

  const removeGradient = (i: number) => {
    setSystem((prev) =>
      prev
        ? { ...prev, gradients: prev.gradients.filter((_, idx) => idx !== i) }
        : prev,
    );
  };

  const updateStop = (gi: number, si: number, patch: Partial<GradientStop>) => {
    setSystem((prev) => {
      if (!prev) return prev;
      const next = prev.gradients.map((g, idx) =>
        idx === gi
          ? {
              ...g,
              stops: g.stops.map((s, sIdx) =>
                sIdx === si ? { ...s, ...patch } : s,
              ),
            }
          : g,
      );
      return { ...prev, gradients: next };
    });
  };

  const addStop = (gi: number) => {
    setSystem((prev) => {
      if (!prev) return prev;
      const next = prev.gradients.map((g, idx) => {
        if (idx !== gi) return g;
        if (g.stops.length >= 4) return g;
        const lastPos = g.stops[g.stops.length - 1]?.pos ?? 0;
        return {
          ...g,
          stops: [...g.stops, { hex: "#888888", pos: Math.min(1, lastPos + 0.25) }],
        };
      });
      return { ...prev, gradients: next };
    });
  };

  const removeStop = (gi: number, si: number) => {
    setSystem((prev) => {
      if (!prev) return prev;
      const next = prev.gradients.map((g, idx) => {
        if (idx !== gi) return g;
        if (g.stops.length <= 2) return g;
        return { ...g, stops: g.stops.filter((_, sIdx) => sIdx !== si) };
      });
      return { ...prev, gradients: next };
    });
  };

  const hasRules =
    !!(system?.voice && system.voice.trim()) ||
    !!(system?.analysis_style && system.analysis_style.trim());

  const runValidator = async () => {
    if (!system || validating) return;
    const sample = validatorSample.trim();
    if (!sample) {
      toast.error("Add some sample text first");
      return;
    }
    if (!hasRules) {
      toast.error("Define a voice or analysis style first");
      return;
    }
    setValidating(true);
    try {
      const { data, error } = await awsClient.functions.invoke(
        "validate-writing-rules",
        {
          body: {
            voice: system.voice ?? "",
            analysis_style: system.analysis_style ?? "",
            sample,
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if(activeSystem.current===system.id)setValidationResult(data);
    } catch (e: any) {
      if(activeSystem.current===system.id)toast.error("Validation failed", { description: e?.message });
    } finally {
      if(activeSystem.current===system.id)setValidating(false);
    }
  };

  const previewTokens = useMemo(() => {
    if (!system) return null;
    const find = (role: string) =>
      system.palette.find((c) => c.role?.toLowerCase() === role)?.hex;
    const resolveSizes = (fmt: "pptx" | "pdf") => {
      const overrides = system.fonts.sizes?.[fmt] ?? {};
      const d = SIZE_DEFAULTS[fmt];
      return {
        headingPt: overrides.headingPt ?? d.headingPt,
        bodyPt: overrides.bodyPt ?? d.bodyPt,
        headingLineHeight: overrides.headingLineHeight ?? d.headingLineHeight,
        bodyLineHeight: overrides.bodyLineHeight ?? d.bodyLineHeight,
      };
    };
    return {
      primary: find("primary") ?? system.palette[0]?.hex ?? "#0F766E",
      accent: find("accent") ?? system.palette[1]?.hex ?? "#14B8A6",
      foreground: find("foreground") ?? "#0F172A",
      background: find("background") ?? "#FFFFFF",
      muted: find("muted") ?? "#64748B",
      heading: system.fonts.heading || "Inter",
      body: system.fonts.body || "Inter",
      sizes: { pptx: resolveSizes("pptx"), pdf: resolveSizes("pdf") },
    };
  }, [system]);

  if (loading || !system || system.id!==systemId || !previewTokens) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Toolbar */}
        <div className="border-b border-border px-4 py-2 flex items-center gap-3 shrink-0">
          <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5">
            <Link to="/design/systems">
              <ArrowLeft className="h-4 w-4" />
              All systems
            </Link>
          </Button>
          <div className="flex-1" />
          {readOnly ? (
            <span className="text-ui-sm text-muted-foreground inline-flex items-center gap-1.5">
              <Globe2 className="h-3.5 w-3.5 text-primary" />
              Shared — read-only
            </span>
          ) : (
            <Button onClick={onSave} disabled={saving || uploadingLogo} size="sm" className="gap-1.5">
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </Button>
          )}
        </div>

        {isMobile && <div className="flex gap-2 border-b border-border px-4 py-2" role="group" aria-label="Design system view">
          <Button size="sm" variant={mobilePane==="editor"?"secondary":"ghost"} aria-pressed={mobilePane==="editor"} onClick={()=>setMobilePane("editor")}>Edit</Button>
          <Button size="sm" variant={mobilePane==="preview"?"secondary":"ghost"} aria-pressed={mobilePane==="preview"} onClick={()=>setMobilePane("preview")}>Preview</Button>
        </div>}
        <ResizablePanelGroup key={isMobile?mobilePane:"desktop"} orientation="horizontal" className="flex-1 min-h-0">
          {/* LEFT: Editor form */}
          {(!isMobile || mobilePane==="editor") && <ResizablePanel defaultSize={isMobile?100:50} minSize={32}>
            <div className="h-full overflow-y-auto">
              <div className="px-6 py-6 space-y-6 max-w-2xl">
                <div>
                  <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                    Name
                  </Label>
                  <Input
                    value={system.name}
                    disabled={readOnly}
                    onChange={(e) => setSystem({ ...system, name: e.target.value })}
                    className="mt-1.5"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={system.is_shared}
                    disabled={readOnly}
                    onCheckedChange={(v) => setSystem({ ...system, is_shared: v })}
                    id="shared"
                  />
                  <Label htmlFor="shared" className="text-ui-base">
                    Share with team (read-only for everyone else)
                  </Label>
                </div>

                {/* Logo */}
                <div>
                  <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                    Logo
                  </Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <div className="h-16 w-16 rounded-ui-sm border border-border bg-card flex items-center justify-center overflow-hidden shrink-0">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt="Logo"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {!readOnly && (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) void onUploadLogo(f);
                              e.target.value = "";
                            }}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1.5 h-8"
                              disabled={uploadingLogo}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {uploadingLogo ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                              {logoUrl ? "Replace" : "Upload"}
                            </Button>
                            {logoUrl && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="gap-1.5 h-8 text-muted-foreground"
                                onClick={removeLogo}
                              >
                                <X className="h-3.5 w-3.5" />
                                Remove
                              </Button>
                            )}
                          </div>
                          <p className="text-ui-xs text-muted-foreground">
                            PNG or SVG with transparent background works best.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Palette */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                      Palette
                    </Label>
                    {!readOnly && (
                      <Button
                        onClick={addSwatch}
                        size="sm"
                        variant="outline"
                        className="gap-1.5 h-7"
                      >
                        <Plus className="h-3 w-3" />
                        Add color
                      </Button>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {system.palette.map((c, i) => (
                      <li
                        key={i}
                        className="flex flex-wrap items-center gap-2 rounded-ui-sm border border-border bg-card p-2"
                      >
                        <input
                          type="color"
                          value={c.hex}
                          disabled={readOnly}
                          onChange={(e) => updatePalette(i, { hex: e.target.value })}
                          className="h-9 w-12 rounded cursor-pointer bg-transparent"
                        />
                        <Input
                          value={c.role || ""}
                          placeholder="role"
                          disabled={readOnly}
                          onChange={(e) => updatePalette(i, { role: e.target.value })}
                          className="min-w-20 flex-1 h-9 text-ui-sm"
                        />
                        <Input
                          value={c.name || ""}
                          placeholder="name"
                          disabled={readOnly}
                          onChange={(e) => updatePalette(i, { name: e.target.value })}
                          className="w-28 h-9 text-ui-sm"
                        />
                        <Input
                          value={c.hex}
                          disabled={readOnly}
                          onChange={(e) => updatePalette(i, { hex: e.target.value })}
                          className="w-24 h-9 font-mono text-ui-sm"
                        />
                        {!readOnly && (
                          <button
                            onClick={() => removeSwatch(i)}
                            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-destructive"
                            aria-label="Remove color"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-ui-xs text-muted-foreground mt-1.5">
                    Use roles like <code className="font-mono">primary</code>,{" "}
                    <code className="font-mono">accent</code>,{" "}
                    <code className="font-mono">foreground</code>,{" "}
                    <code className="font-mono">background</code>,{" "}
                    <code className="font-mono">muted</code> so the agent maps them
                    correctly.
                  </p>
                </div>

                {/* Gradients */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                      Gradients
                    </Label>
                    {!readOnly && (
                      <Button
                        onClick={addGradient}
                        size="sm"
                        variant="outline"
                        className="gap-1.5 h-7"
                      >
                        <Plus className="h-3 w-3" />
                        Add gradient
                      </Button>
                    )}
                  </div>
                  {system.gradients.length === 0 ? (
                    <p className="text-ui-xs text-muted-foreground">
                      Optional. Add a brand gradient if your identity uses one for hero
                      slides or section dividers (e.g. teal → lime).
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {system.gradients.map((g, gi) => (
                        <li
                          key={g.id}
                          className="rounded-ui-sm border border-border bg-card p-3 space-y-2"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="h-9 w-16 rounded border border-border shrink-0"
                              style={{ background: gradientCss(g) }}
                              aria-label="Gradient preview"
                            />
                            <Input
                              value={g.name || ""}
                              placeholder="name"
                              disabled={readOnly}
                              onChange={(e) =>
                                updateGradient(gi, { name: e.target.value })
                              }
                              className="min-w-20 flex-1 h-9 text-ui-sm"
                            />
                            <select
                              value={g.role || ""}
                              disabled={readOnly}
                              onChange={(e) =>
                                updateGradient(gi, {
                                  role: e.target.value as Gradient["role"],
                                })
                              }
                              className="h-9 text-ui-sm rounded-ui-sm border border-input bg-background px-2"
                            >
                              <option value="">role</option>
                              <option value="hero">hero</option>
                              <option value="section">section</option>
                              <option value="accent">accent</option>
                            </select>
                            {!readOnly && (
                              <button
                                onClick={() => removeGradient(gi)}
                                className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-destructive"
                                aria-label="Remove gradient"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-ui-xs uppercase tracking-wider text-muted-foreground w-12">
                              Angle
                            </span>
                            <input
                              type="range"
                              min={0}
                              max={360}
                              step={1}
                              value={g.angle}
                              disabled={readOnly}
                              onChange={(e) =>
                                updateGradient(gi, { angle: parseInt(e.target.value) })
                              }
                              className="flex-1"
                            />
                            <span className="text-ui-xs font-mono w-10 text-right text-foreground">
                              {g.angle}°
                            </span>
                          </div>

                          <div className="space-y-1.5">
                            {g.stops.map((s, si) => (
                              <div key={si} className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={s.hex}
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    updateStop(gi, si, { hex: e.target.value })
                                  }
                                  className="h-7 w-10 rounded cursor-pointer bg-transparent"
                                />
                                <Input
                                  value={s.hex}
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    updateStop(gi, si, { hex: e.target.value })
                                  }
                                  className="w-24 h-7 font-mono text-ui-xs"
                                />
                                <input
                                  type="range"
                                  min={0}
                                  max={1}
                                  step={0.01}
                                  value={s.pos}
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    updateStop(gi, si, { pos: parseFloat(e.target.value) })
                                  }
                                  className="flex-1"
                                />
                                <span className="text-ui-xs font-mono w-8 text-right text-muted-foreground">
                                  {Math.round(s.pos * 100)}%
                                </span>
                                {!readOnly && g.stops.length > 2 && (
                                  <button
                                    onClick={() => removeStop(gi, si)}
                                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-destructive"
                                    aria-label="Remove stop"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                            {!readOnly && g.stops.length < 4 && (
                              <button
                                type="button"
                                onClick={() => addStop(gi)}
                                className="text-ui-xs text-muted-foreground hover:text-foreground underline"
                              >
                                + add stop
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                      Heading font
                    </Label>
                    <Input
                      value={system.fonts.heading || ""}
                      disabled={readOnly}
                      placeholder="Inter, Georgia, …"
                      list="font-presets"
                      onChange={(e) =>
                        setSystem({
                          ...system,
                          fonts: { ...system.fonts, heading: e.target.value },
                        })
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                      Body font
                    </Label>
                    <Input
                      value={system.fonts.body || ""}
                      disabled={readOnly}
                      placeholder="Inter, Calibri, …"
                      list="font-presets"
                      onChange={(e) =>
                        setSystem({
                          ...system,
                          fonts: { ...system.fonts, body: e.target.value },
                        })
                      }
                      className="mt-1.5"
                    />
                  </div>
                  <datalist id="font-presets">
                    {FONT_PRESETS.map((f) => (
                      <option key={f} value={f} />
                    ))}
                  </datalist>
                </div>

                {/* Typography size overrides */}
                <TypographyOverrides
                  system={system}
                  setSystem={setSystem}
                  readOnly={readOnly}
                />

                <div>
                  <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                    Writing voice
                  </Label>
                  <Textarea
                    value={system.voice || ""}
                    disabled={readOnly}
                    placeholder="e.g. Direct, plainspoken, no jargon. Lead with the implication."
                    onChange={(e) => setSystem({ ...system, voice: e.target.value })}
                    className="mt-1.5 min-h-[100px]"
                  />
                </div>

                <div>
                  <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
                    Analysis style
                  </Label>
                  <Textarea
                    value={system.analysis_style || ""}
                    disabled={readOnly}
                    placeholder="e.g. Always pair quantitative findings with one qualitative quote. Always recommend a next action."
                    onChange={(e) =>
                      setSystem({ ...system, analysis_style: e.target.value })
                    }
                    className="mt-1.5 min-h-[100px]"
                  />
                </div>
              </div>
            </div>
          </ResizablePanel>}

          {!isMobile && <ResizableHandle withHandle />}

          {/* RIGHT: Live preview */}
          {(!isMobile || mobilePane==="preview") && <ResizablePanel defaultSize={isMobile?100:50} minSize={28}>
            <div className="h-full overflow-y-auto bg-muted/30">
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                    Export preview
                  </span>
                  <div className="inline-flex rounded-ui-sm border border-border bg-card p-0.5">
                    <button
                      type="button"
                      onClick={() => setPreviewFormat("pptx")}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-ui-sm font-bold transition-colors ${
                        previewFormat === "pptx"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      aria-pressed={previewFormat === "pptx"}
                    >
                      <Presentation className="h-3.5 w-3.5" />
                      PPTX
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewFormat("pdf")}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-ui-sm font-bold transition-colors ${
                        previewFormat === "pdf"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      aria-pressed={previewFormat === "pdf"}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      PDF
                    </button>
                  </div>
                </div>

                <div className="text-ui-xs text-muted-foreground -mt-2">
                  {previewFormat === "pptx"
                    ? "16:9 slide — large display type, condensed copy."
                    : "Letter page — flowing prose, hierarchical headings."}
                </div>

                {previewFormat === "pptx" ? (
                  /* PPTX: 16:9 slide */
                  <ScaledPreview width={1280} height={720}
                    style={{
                      background: previewTokens.background,
                      color: previewTokens.foreground,
                      fontFamily: `${previewTokens.body}, system-ui, sans-serif`,
                    }}
                  >
                    <div className="h-full w-full p-8 flex flex-col">
                      <div className="flex items-start justify-between mb-6">
                        <div
                          className="text-ui-xs tracking-[0.2em] uppercase font-bold"
                          style={{ color: previewTokens.primary }}
                        >
                          {system.name || "Brand"}
                        </div>
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt=""
                            className="h-8 max-w-[120px] object-contain"
                          />
                        )}
                      </div>

                      <h1
                        className="font-bold mb-3"
                        style={{
                          fontFamily: `${previewTokens.heading}, ${previewTokens.body}, serif`,
                          color: previewTokens.foreground,
                          fontSize: `${previewTokens.sizes.pptx.headingPt}pt`,
                          lineHeight: previewTokens.sizes.pptx.headingLineHeight,
                        }}
                      >
                        A declarative claim, not a label.
                      </h1>
                      <p
                        className="mb-4"
                        style={{
                          color: previewTokens.muted,
                          fontSize: `${previewTokens.sizes.pptx.bodyPt}pt`,
                          lineHeight: previewTokens.sizes.pptx.bodyLineHeight,
                        }}
                      >
                        Body copy renders in your selected body font with muted
                        color for secondary detail. Quotes, callouts, and bullets
                        inherit from these tokens.
                      </p>

                      <div
                        className="rounded-ui-sm p-3 text-ui-sm leading-relaxed mt-auto"
                        style={{
                          background: `${previewTokens.primary}12`,
                          borderLeft: `3px solid ${previewTokens.accent}`,
                          color: previewTokens.foreground,
                        }}
                      >
                        <strong style={{ color: previewTokens.primary }}>
                          Recommendation —
                        </strong>{" "}
                        Imperative action statement that the reader can act on
                        this week.
                      </div>
                    </div>
                  </ScaledPreview>
                ) : (
                  /* PDF: letter page (8.5 x 11 portrait) */
                  <ScaledPreview width={816} height={1056}
                    style={{
                      background: previewTokens.background,
                      color: previewTokens.foreground,
                      fontFamily: `${previewTokens.body}, system-ui, sans-serif`,
                    }}
                  >
                    <div className="h-full w-full px-10 py-10 flex flex-col">
                      <div
                        className="flex items-center justify-between pb-3 mb-5 border-b"
                        style={{ borderColor: `${previewTokens.muted}33` }}
                      >
                        <div
                          className="text-ui-xs tracking-[0.18em] uppercase font-bold"
                          style={{ color: previewTokens.primary }}
                        >
                          {system.name || "Brand"} · Report
                        </div>
                        {logoUrl && (
                          <img
                            src={logoUrl}
                            alt=""
                            className="h-6 max-w-[100px] object-contain"
                          />
                        )}
                      </div>

                      <h1
                        className="font-bold mb-1"
                        style={{
                          fontFamily: `${previewTokens.heading}, ${previewTokens.body}, serif`,
                          color: previewTokens.foreground,
                          fontSize: `${previewTokens.sizes.pdf.headingPt}pt`,
                          lineHeight: previewTokens.sizes.pdf.headingLineHeight,
                        }}
                      >
                        A declarative claim, not a label.
                      </h1>
                      <p
                        className="text-ui-xs mb-4"
                        style={{ color: previewTokens.muted }}
                      >
                        Prepared for the executive team · April 2026
                      </p>

                      <h2
                        className="font-bold mb-1.5"
                        style={{
                          fontFamily: `${previewTokens.heading}, serif`,
                          color: previewTokens.primary,
                          fontSize: `${Math.max(10, previewTokens.sizes.pdf.headingPt * 0.55)}pt`,
                          lineHeight: previewTokens.sizes.pdf.headingLineHeight,
                        }}
                      >
                        Summary
                      </h2>
                      <p
                        className="mb-3"
                        style={{
                          color: previewTokens.foreground,
                          fontSize: `${previewTokens.sizes.pdf.bodyPt}pt`,
                          lineHeight: previewTokens.sizes.pdf.bodyLineHeight,
                        }}
                      >
                        Body copy renders in your selected body font at the
                        density appropriate for printed reports. Paragraphs flow
                        with comfortable line-height and hierarchical headings.
                      </p>

                      <h2
                        className="font-bold mb-1.5"
                        style={{
                          fontFamily: `${previewTokens.heading}, serif`,
                          color: previewTokens.primary,
                          fontSize: `${Math.max(10, previewTokens.sizes.pdf.headingPt * 0.55)}pt`,
                          lineHeight: previewTokens.sizes.pdf.headingLineHeight,
                        }}
                      >
                        Findings
                      </h2>
                      <ul
                        className="space-y-1 mb-4 pl-4 list-disc marker:text-current"
                        style={{
                          color: previewTokens.foreground,
                          fontSize: `${previewTokens.sizes.pdf.bodyPt}pt`,
                          lineHeight: previewTokens.sizes.pdf.bodyLineHeight,
                        }}
                      >
                        <li>First finding stated as a complete claim.</li>
                        <li>Second finding with a supporting figure inline.</li>
                        <li>Third finding pointing to a downstream action.</li>
                      </ul>

                      <div
                        className="rounded-ui-sm p-3 text-ui-xs leading-relaxed mt-auto"
                        style={{
                          background: `${previewTokens.primary}10`,
                          borderLeft: `3px solid ${previewTokens.accent}`,
                          color: previewTokens.foreground,
                        }}
                      >
                        <strong style={{ color: previewTokens.primary }}>
                          Recommendation —
                        </strong>{" "}
                        Imperative action statement the reader can act on this
                        week.
                      </div>
                    </div>
                  </ScaledPreview>
                )}

                {/* Token chips */}
                <div className="rounded-ui-sm border border-border bg-card p-4 space-y-3">
                  <div className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                    Resolved tokens
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-ui-sm">
                    {(["primary", "accent", "foreground", "background", "muted"] as const).map(
                      (role) => (
                        <div key={role} className="flex items-center gap-2">
                          <span
                            className="h-5 w-5 rounded border border-border shrink-0"
                            style={{ background: previewTokens[role] }}
                          />
                          <span className="text-muted-foreground capitalize w-20">
                            {role}
                          </span>
                          <span className="font-mono text-ui-xs text-foreground">
                            {previewTokens[role]}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                  <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-ui-sm">
                    <div>
                      <div className="text-muted-foreground text-ui-xs">Heading</div>
                      <div
                        className="text-ui-lg font-bold mt-0.5"
                        style={{
                          fontFamily: `${previewTokens.heading}, serif`,
                        }}
                      >
                        {previewTokens.heading}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-ui-xs">Body</div>
                      <div
                        className="text-ui-base mt-0.5"
                        style={{
                          fontFamily: `${previewTokens.body}, sans-serif`,
                        }}
                      >
                        {previewTokens.body}
                      </div>
                    </div>
                  </div>
                </div>

                {(system.voice || system.analysis_style) && (
                  <div className="rounded-ui-sm border border-border bg-card p-4 space-y-3">
                    <div className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                      Writing rules in effect
                    </div>
                    {system.voice && (
                      <div>
                        <div className="text-ui-xs text-muted-foreground mb-1">
                          Voice
                        </div>
                        <p className="text-ui-sm text-foreground whitespace-pre-wrap">
                          {system.voice}
                        </p>
                      </div>
                    )}
                    {system.analysis_style && (
                      <div>
                        <div className="text-ui-xs text-muted-foreground mb-1">
                          Analysis style
                        </div>
                        <p className="text-ui-sm text-foreground whitespace-pre-wrap">
                          {system.analysis_style}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Writing-rules validator */}
                <div className="rounded-ui-sm border border-border bg-card p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                      <span className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                        Writing-rules validator
                      </span>
                    </div>
                    {validationResult && (
                      <span
                        className={`text-ui-xs font-mono ${
                          validationResult.score >= 85
                            ? "text-primary"
                            : validationResult.score >= 60
                              ? "text-ui-warning-icon"
                              : "text-destructive"
                        }`}
                      >
                        {validationResult.score}/100
                      </span>
                    )}
                  </div>

                  <p className="text-ui-xs text-muted-foreground">
                    Paste sample copy the agent might generate; we'll flag every
                    line that breaks your voice or analysis-style rules.
                  </p>

                  <Textarea
                    value={validatorSample}
                    onChange={(e) => setValidatorSample(e.target.value)}
                    placeholder="Paste sample text to validate against your writing rules…"
                    className="min-h-[140px] text-ui-sm font-mono"
                    disabled={validating}
                  />

                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ui-xs text-muted-foreground">
                      {hasRules
                        ? "Checks against voice + analysis style"
                        : "Add a voice or analysis style above to enable"}
                    </span>
                    <Button
                      size="sm"
                      onClick={runValidator}
                      disabled={validating || !hasRules || !validatorSample.trim()}
                      className="gap-1.5 h-8"
                    >
                      {validating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ShieldCheck className="h-3.5 w-3.5" />
                      )}
                      Validate
                    </Button>
                  </div>

                  {validationResult && (
                    <div className="space-y-2 pt-1 border-t border-border">
                      <div className="flex items-start gap-2 pt-2">
                        {validationResult.findings.length === 0 ? (
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        ) : (
                          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                        )}
                        <p className="text-ui-sm text-foreground">
                          {validationResult.summary}
                        </p>
                      </div>

                      {validationResult.findings.length === 0 ? (
                        <p className="text-ui-xs text-muted-foreground pl-6">
                          No mismatches detected.
                        </p>
                      ) : (
                        <ul className="space-y-2">
                          {validationResult.findings.map((f, i) => {
                            const Icon =
                              f.severity === "error"
                                ? AlertCircle
                                : f.severity === "warning"
                                  ? AlertTriangle
                                  : Info;
                            const iconColor =
                              f.severity === "error"
                                ? "text-destructive"
                                : f.severity === "warning"
                                  ? "text-ui-warning-icon"
                                  : "text-muted-foreground";
                            const borderColor =
                              f.severity === "error"
                                ? "border-l-destructive"
                                : f.severity === "warning"
                                  ? "border-l-ui-warning-icon"
                                  : "border-l-muted-foreground";
                            return (
                              <li
                                key={i}
                                className={`rounded-ui-sm border border-border border-l-2 ${borderColor} bg-background p-2.5 space-y-1.5`}
                              >
                                <div className="flex items-start gap-2">
                                  <Icon
                                    className={`h-3.5 w-3.5 ${iconColor} shrink-0 mt-0.5`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-ui-xs uppercase tracking-wider font-bold text-muted-foreground">
                                        {f.severity}
                                      </span>
                                      <span className="text-ui-xs uppercase tracking-wider text-muted-foreground">
                                        ·{" "}
                                        {f.rule_source === "voice"
                                          ? "Voice"
                                          : "Analysis style"}
                                      </span>
                                    </div>
                                    <p className="text-ui-sm text-foreground mt-1">
                                      <span className="font-bold">Rule:</span>{" "}
                                      {f.rule}
                                    </p>
                                  </div>
                                </div>
                                <blockquote className="text-ui-xs text-muted-foreground italic border-l-2 border-border pl-2 ml-5">
                                  "{f.excerpt}"
                                </blockquote>
                                <p className="text-ui-xs text-foreground ml-5">
                                  {f.explanation}
                                </p>
                                <p className="text-ui-xs ml-5">
                                  <span className="text-primary font-bold">
                                    Suggest:
                                  </span>{" "}
                                  <span className="text-foreground">
                                    {f.suggestion}
                                  </span>
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ResizablePanel>}
        </ResizablePanelGroup>
      </div>
    </AppLayout>
  );
}

type TypographyOverridesProps = {
  system: System;
  setSystem: React.Dispatch<React.SetStateAction<System | null>>;
  readOnly: boolean;
};

function TypographyOverrides({
  system,
  setSystem,
  readOnly,
}: TypographyOverridesProps) {
  const [format, setFormat] = useState<"pptx" | "pdf">("pptx");
  const overrides = system.fonts.sizes?.[format] ?? {};
  const defaults = SIZE_DEFAULTS[format];

  const update = (patch: Partial<SizeOverrides>) => {
    setSystem((prev) => {
      if (!prev) return prev;
      const sizes = { ...(prev.fonts.sizes ?? {}) };
      sizes[format] = { ...(sizes[format] ?? {}), ...patch };
      return { ...prev, fonts: { ...prev.fonts, sizes } };
    });
  };

  const resetAll = () => {
    setSystem((prev) => {
      if (!prev) return prev;
      const sizes = { ...(prev.fonts.sizes ?? {}) };
      delete sizes[format];
      return { ...prev, fonts: { ...prev.fonts, sizes } };
    });
  };

  const isOverridden = (key: keyof SizeOverrides) =>
    overrides[key] !== undefined && overrides[key] !== defaults[key];

  const fields: Array<{
    key: keyof SizeOverrides;
    label: string;
    suffix: string;
    step: number;
    min: number;
    max: number;
  }> = [
    { key: "headingPt", label: "Heading size", suffix: "pt", step: 1, min: 8, max: 96 },
    { key: "bodyPt", label: "Body size", suffix: "pt", step: 1, min: 6, max: 48 },
    {
      key: "headingLineHeight",
      label: "Heading line-height",
      suffix: "×",
      step: 0.05,
      min: 0.9,
      max: 2,
    },
    {
      key: "bodyLineHeight",
      label: "Body line-height",
      suffix: "×",
      step: 0.05,
      min: 1,
      max: 2.5,
    },
  ];

  const hasAnyOverride = fields.some((f) => isOverridden(f.key));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-ui-sm uppercase tracking-wider text-muted-foreground">
          Typography overrides
        </Label>
        <div className="inline-flex rounded-ui-sm border border-border bg-card p-0.5">
          {(["pptx", "pdf"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFormat(f)}
              className={`px-2 py-0.5 rounded text-ui-xs font-bold uppercase tracking-wider transition-colors ${
                format === f
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <p className="text-ui-xs text-muted-foreground -mt-1">
        Defaults come from your system tokens. Overrides apply only to this format
        and the export preview updates instantly.
      </p>

      <div className="grid grid-cols-2 gap-2">
        {fields.map((f) => {
          const current = overrides[f.key] ?? defaults[f.key];
          const overridden = isOverridden(f.key);
          return (
            <div
              key={f.key}
              className="rounded-ui-sm border border-border bg-card p-2.5"
            >
              <div className="flex items-center justify-between gap-1 mb-1">
                <span className="text-ui-xs text-muted-foreground">
                  {f.label}
                </span>
                {overridden && !readOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...overrides };
                      delete next[f.key];
                      setSystem((prev) =>
                        prev
                          ? {
                              ...prev,
                              fonts: {
                                ...prev.fonts,
                                sizes: {
                                  ...(prev.fonts.sizes ?? {}),
                                  [format]: next,
                                },
                              },
                            }
                          : prev,
                      );
                    }}
                    className="text-ui-xs text-muted-foreground hover:text-foreground"
                    title="Reset to default"
                  >
                    reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step={f.step}
                  min={f.min}
                  max={f.max}
                  value={current}
                  disabled={readOnly}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (Number.isNaN(v)) return;
                    update({ [f.key]: v });
                  }}
                  className={`h-8 text-ui-sm font-mono ${
                    overridden ? "border-primary/50" : ""
                  }`}
                />
                <span className="text-ui-xs text-muted-foreground shrink-0">
                  {f.suffix}
                </span>
              </div>
              <div className="text-ui-xs text-muted-foreground mt-1">
                Default: {defaults[f.key]}
                {f.suffix}
              </div>
            </div>
          );
        })}
      </div>

      {hasAnyOverride && !readOnly && (
        <button
          type="button"
          onClick={resetAll}
          className="text-ui-xs text-muted-foreground hover:text-foreground underline"
        >
          Reset all {format.toUpperCase()} overrides to defaults
        </button>
      )}
    </div>
  );
}

/** Keep export typography in physical page proportions at any panel width. */
function ScaledPreview({width,height,style,children}:{width:number;height:number;style:React.CSSProperties;children:React.ReactNode}){
 const container=useRef<HTMLDivElement>(null);
 const [availableWidth,setAvailableWidth]=useState(0);
 useEffect(()=>{
  const element=container.current;if(!element)return;
  const measure=()=>setAvailableWidth(element.clientWidth);
  const observer=new ResizeObserver(measure);observer.observe(element);measure();
  return()=>observer.disconnect();
 },[]);
 return <div ref={container} className="relative w-full overflow-hidden rounded-ui-md border border-border " style={{aspectRatio:`${width} / ${height}`}}>
  <div style={{...style,width,height,position:"absolute",inset:0,transform:`scale(${availableWidth/width})`,transformOrigin:"top left"}}>{children}</div>
 </div>;
}
